/**
 * The AMS and its iOS app open the widget pages with the token of the signed in user: `<widget page>#token=<token>`.
 * The widget then calls the API as that user instead of with the company API key. A fragment is not sent to the
 * server; it is taken out of the address right away, so it is not left in a copied link, and kept in sessionStorage,
 * so it lives as long as the tab and survives a reload.
 */
const TOKEN_PARAM = 'token'
const STORAGE_KEY = 'hd-widget:ams-token'

// the token when sessionStorage is unavailable (blocked site data, some private modes)
let memoryToken: string | null = null

const getSessionStorage = (): Storage | null => {
  try {
    // reading window.sessionStorage itself throws when the site data is blocked
    return window.sessionStorage
  } catch {
    return null
  }
}

// moves the token from the fragment of the current address to the tab, see above
export const takeAuthTokenFromURL = () => {
  const params = new URLSearchParams(window.location.hash.slice(1))
  const token = params.get(TOKEN_PARAM)
  if (!token) {
    return
  }
  params.delete(TOKEN_PARAM)
  const hash = params.toString()
  const {pathname, search} = window.location
  window.history.replaceState(window.history.state, '', `${pathname}${search}${hash ? `#${hash}` : ''}`)

  memoryToken = token
  try {
    getSessionStorage()?.setItem(STORAGE_KEY, token)
  } catch {
    // unavailable, the token lives in memory until the page is left
  }
}

// the token the tab got from the AMS, null without one
export const getAuthToken = (): string | null => {
  if (memoryToken) {
    return memoryToken
  }
  try {
    return getSessionStorage()?.getItem(STORAGE_KEY) ?? null
  } catch {
    return null
  }
}

// the Authorization header: the token of the AMS user when the tab has one, the company API key otherwise
export const authorization = (apiKey: string | null): string => {
  const token = getAuthToken()
  return token ? `Token ${token}` : `Api-Key ${apiKey}`
}

// the API refused the token of the AMS user: it expired or the user signed out
export const isTokenRejected = (error: unknown): boolean => {
  const status = (error as { status?: number } | null)?.status
  return getAuthToken() !== null && (status === 401 || status === 403)
}

// forgets the token, for the tests
export const clearAuthToken = () => {
  memoryToken = null
  try {
    getSessionStorage()?.removeItem(STORAGE_KEY)
  } catch {
    // unavailable, nothing stored
  }
}
